<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// State
const account = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const error = ref(null);

// Form fields
const profileImage = ref('');
const businessDescription = ref('');
const status = ref('');
const businessAddress = ref('');
const businessEmail = ref('');
const businessWebsites = ref(['']);
const vertical = ref('OTHER');
const connectedPhoneNumber = ref('');
const businessName = ref('');

// File input ref
const fileInputRef = ref(null);

// Options
const verticalOptions = [
  { value: 'UNDEFINED', label: 'Não definido' },
  { value: 'OTHER', label: 'Outro' },
  { value: 'AUTO', label: 'Automotivo' },
  { value: 'BEAUTY', label: 'Beleza e bem-estar' },
  { value: 'CLOTHING', label: 'Vestuário' },
  { value: 'EDUCATION', label: 'Educação' },
  { value: 'ENTERTAINMENT', label: 'Entretenimento' },
  { value: 'EVENT_PLANNING', label: 'Planejamento de eventos' },
  { value: 'FINANCIAL', label: 'Serviços financeiros' },
  { value: 'GROCERY', label: 'Mercados e mercearias' },
  { value: 'GOVERNMENT', label: 'Governo' },
  { value: 'HOTEL_AND_LODGING', label: 'Hotéis e hospedagem' },
  { value: 'MEDICAL_AND_HEALTH', label: 'Saúde' },
  { value: 'NONPROFIT', label: 'Organização sem fins lucrativos' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Serviços profissionais' },
  { value: 'RESTAURANT', label: 'Restaurante' },
  { value: 'RETAIL', label: 'Varejo' },
  { value: 'TRAVEL', label: 'Viagens' }
];

// Get inbox ID from route
const inboxId = route.params.inboxId;

const businessDescriptionCount = computed(() => businessDescription.value?.length || 0);
const statusCount = computed(() => status.value?.length || 0);
const sanitizedWebsites = computed(() =>
  businessWebsites.value
    .map(website => website.trim())
    .filter(website => website.length > 0)
);

const headerTitle = computed(() => {
  if (businessName.value) {
    return `Perfil do WhatsApp: ${businessName.value}`;
  }

  if (account.value) {
    return `Perfil do WhatsApp: ${account.value.inbox_name || account.value.display_phone_number}`;
  }

  return 'Perfil do WhatsApp';
});

const resetProfileFields = () => {
  profileImage.value = '';
  businessDescription.value = '';
  status.value = '';
  businessAddress.value = '';
  businessEmail.value = '';
  businessWebsites.value = [''];
  vertical.value = 'OTHER';
  connectedPhoneNumber.value = '';
  businessName.value = '';
};

const applyProfile = profile => {
  const safeProfile = profile || {};
  profileImage.value = safeProfile.profile_picture_url || '';
  businessDescription.value = safeProfile.description || '';
  status.value = safeProfile.about || '';
  businessAddress.value = safeProfile.address || '';
  businessEmail.value = safeProfile.email || '';
  vertical.value = safeProfile.vertical || vertical.value || 'OTHER';
  connectedPhoneNumber.value =
    safeProfile.display_phone_number || safeProfile.phone_number || connectedPhoneNumber.value;
  businessName.value = safeProfile.business_name || businessName.value;

  const websites = Array.isArray(safeProfile.websites)
    ? safeProfile.websites.filter(site => typeof site === 'string' && site.trim().length > 0)
    : [];
  businessWebsites.value = websites.length ? websites : [''];
};

const addWebsite = () => {
  businessWebsites.value = [...businessWebsites.value, ''];
};

const removeWebsite = index => {
  if (businessWebsites.value.length <= 1) {
    businessWebsites.value = [''];
    return;
  }

  businessWebsites.value = businessWebsites.value.filter((_, websiteIndex) => websiteIndex !== index);
};

// Trigger file input click
const triggerFileInput = () => {
  fileInputRef.value?.click();
};

// Handle file upload
const handleFileUpload = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  // Validate file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!validTypes.includes(file.type)) {
    useAlert('Por favor, selecione uma imagem JPG ou PNG', 'error');
    return;
  }

  // Validate file size (5MB max)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    useAlert('A imagem deve ter no máximo 5MB', 'error');
    return;
  }

  if (!account.value) return;

  isSaving.value = true;

  try {
    const formData = new FormData();
    formData.append('profile_picture', file);

    // Get auth headers without Content-Type (let browser set it for FormData)
    const authHeaders = getAuthHeaders();
    delete authHeaders['Content-Type'];

    const response = await fetch(
      `/api/v1/whatsapp/${account.value.inbox_id}/business_profile/upload_picture`,
      {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = data?.error || 'Não foi possível enviar a imagem';
      throw new Error(message);
    }

    // Update profile image with the new URL
    if (data.profile_picture_url) {
      profileImage.value = data.profile_picture_url;
      useAlert('Imagem do perfil atualizada com sucesso!', 'success');
    }
  } catch (error) {
    console.error('Failed to upload profile picture:', error);
    useAlert(error.message || 'Não foi possível enviar a imagem', 'error');
  } finally {
    isSaving.value = false;
    // Reset file input
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }
  }
};

// Get authentication headers from cookie
const getAuthHeaders = () => {
  const getCookie = name => {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)')
    );
    return match ? decodeURIComponent(match[2]) : null;
  };

  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
      'Content-Type': 'application/json',
    };
  } catch (e) {
    console.error('Error parsing session cookie:', e);
    return {};
  }
};

// Carrega dados do perfil no Chatwoot
const loadAccountProfile = async () => {
  isLoading.value = true;
  error.value = null;
  resetProfileFields();

  try {
    // Load account basic info from load_inboxes
    const response = await fetch('/api/v1/bulk_messages/load_inboxes', {
      headers: getAuthHeaders(),
    });

    if (response.ok) {
      const data = await response.json();
      const foundAccount = data.results.find(acc => acc.inbox_id.toString() === inboxId);

      if (foundAccount) {
        account.value = foundAccount;
        connectedPhoneNumber.value =
          foundAccount.display_phone_number ||
          foundAccount.phone_number ||
          connectedPhoneNumber.value;
        businessName.value = foundAccount.inbox_name || businessName.value;

        // Carrega dados do perfil no WhatsApp Business
        await loadWhatsAppProfile(foundAccount.inbox_id);
      } else {
        error.value = 'Conta não encontrada';
        useAlert('Conta não encontrada', 'error');
      }
    } else {
      const errorData = await response.json();
      error.value = errorData.error || 'Não foi possível carregar a conta';
      useAlert(error.value, 'error');
    }
  } catch (err) {
    console.error('Failed to load account profile:', err);
    error.value = 'Não foi possível carregar o perfil';
    useAlert(error.value, 'error');
  } finally {
    isLoading.value = false;
  }
};

// Busca o perfil do WhatsApp Business (integração com a Meta API)
const loadWhatsAppProfile = async inboxIdentifier => {
  if (!inboxIdentifier) return;

  try {
    const response = await fetch(`/api/v1/whatsapp/${inboxIdentifier}/business_profile`, {
      headers: getAuthHeaders(),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = payload?.error || 'Não foi possível carregar o perfil do WhatsApp';
      throw new Error(message);
    }

    applyProfile(payload.profile);
  } catch (error) {
    console.error('Failed to load WhatsApp profile:', error);
    useAlert(error.message || 'Não foi possível carregar o perfil do WhatsApp', 'error');
  }
};

// Salva alterações no perfil
const saveProfile = async () => {
  if (!account.value) return;

  isSaving.value = true;

  try {
    const payload = {
      description: businessDescription.value || '',
      about: status.value || '',
      address: businessAddress.value || '',
      email: businessEmail.value || '',
      vertical: vertical.value || 'OTHER',
      websites: sanitizedWebsites.value,
    };

    const response = await fetch(
      `/api/v1/whatsapp/${account.value.inbox_id}/business_profile`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ profile: payload }),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = data?.error || 'Não foi possível salvar o perfil';
      throw new Error(message);
    }

    applyProfile(data.profile);
    useAlert('Perfil do WhatsApp atualizado com sucesso!', 'success');
  } catch (error) {
    console.error('Failed to save profile:', error);
    useAlert(error.message || 'Não foi possível salvar o perfil', 'error');
  } finally {
    isSaving.value = false;
  }
};

// Abre a lista de templates filtrada por esta conta
const navigateToAccountTemplates = () => {
  router.push({
    name: 'settings_whatsapp_templates',
    query: { accountId: inboxId }
  });
};

// Volta para a lista de contas
const goBack = () => {
  router.push({ name: 'settings_whatsapp_accounts' });
};

// Formata datas para exibição
const formatDate = (dateString) => {
  if (!dateString) return 'Nunca';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Inicializa ao carregar o componente
onMounted(() => {
  loadAccountProfile();
});
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="headerTitle"
        description="Configure o perfil do seu WhatsApp Business"
        feature-name="whatsapp"
      />
    </template>
    <template #body>
      <!-- Estado de carregamento -->
      <div v-if="isLoading" class="flex justify-center items-center py-8">
        <Spinner size="medium" />
      </div>

      <!-- Estado de erro -->
      <div v-else-if="error" class="rounded-2xl border border-n-ruby-4 bg-n-ruby-1 px-6 py-8 text-sm leading-6 text-n-ruby-11">
        <div class="flex items-center gap-2">
          <i class="i-lucide-alert-circle" />
          Erro ao carregar o perfil: {{ error }}
        </div>
      </div>

      <!-- Perfil da conta -->
      <div v-else-if="account" class="space-y-6">
        <!-- Cabeçalho com ações -->
        <div class="flex justify-between items-center mb-6">
          <Button
            label="← Voltar para contas"
            variant="outline"
            color="slate"
            size="sm"
            @click="goBack"
          />

          <Button
            label="Gerenciar templates"
            variant="solid"
            color="blue"
            size="sm"
            @click="navigateToAccountTemplates"
          >
            <template #icon>
              <i class="i-lucide-file-text" />
            </template>
          </Button>
        </div>

        <div class="bg-n-blue-1 border border-n-blue-border rounded-lg p-5 text-sm text-n-blue-text">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="space-y-1">
              <p class="text-xs font-medium uppercase tracking-wide text-n-blue-text/70">
                Número conectado
              </p>
              <p class="text-2xl font-semibold text-n-blue-text">
                {{ connectedPhoneNumber || 'Não disponível' }}
              </p>
            </div>
            <div class="text-xs text-n-blue-text/70">
              {{ account.integration_type === 'official' ? 'API oficial do WhatsApp Business' : account.integration_type }}
            </div>
          </div>
        </div>

        <!-- Configurações do perfil do WhatsApp Business -->
        <div class="bg-n-alpha-2 rounded-lg p-6 border border-n-weak">
          <h3 class="text-lg font-medium text-n-slate-12 mb-4 flex items-center gap-2">
            <i class="i-lucide-user-circle" />
            <p v-if="businessName" class="text-sm font-medium text-n-blue-text/90">
                {{ businessName }}
              </p>
          </h3>

          <div class="space-y-6">
            <!-- Imagem do perfil -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-2">Imagem do perfil</label>
                <div class="flex items-center gap-4">
                  <div class="w-20 h-20 bg-n-slate-2 rounded-full flex items-center justify-center">
                    <i v-if="!profileImage" class="i-lucide-user text-n-slate-9 text-2xl" />
                    <img v-else :src="profileImage" alt="Perfil" class="w-full h-full rounded-full object-cover" />
                  </div>
                  <div class="flex-1">
                    <!-- Hidden file input -->
                    <input
                      ref="fileInputRef"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      class="hidden"
                      @change="handleFileUpload"
                    />
                    <Button
                      :label="isSaving ? 'Enviando...' : 'Enviar imagem'"
                      variant="outline"
                      color="slate"
                      size="sm"
                      :disabled="isSaving"
                      @click="triggerFileInput"
                    >
                      <template #icon>
                        <i class="i-lucide-upload" />
                      </template>
                    </Button>
                    <p class="text-xs text-n-slate-11 mt-1">JPG ou PNG, máx. 5MB</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Descrição do negócio -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">Descrição do negócio</label>
              <textarea
                v-model="businessDescription"
                class="w-full px-3 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
                rows="4"
                maxlength="512"
                placeholder="Descreva o seu negócio..."
              />
              <p class="text-xs text-n-slate-11 mt-1">{{ businessDescriptionCount }}/512 caracteres</p>
            </div>

            <!-- Mensagem de status -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">Mensagem de status</label>
              <input
                v-model="status"
                type="text"
                class="w-full px-3 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
                maxlength="139"
                placeholder="Disponível"
              />
              <p class="text-xs text-n-slate-11 mt-1">{{ statusCount }}/139 caracteres</p>
            </div>

            <!-- Endereço -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">Endereço comercial</label>
              <input
                v-model="businessAddress"
                type="text"
                class="w-full px-3 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
                maxlength="256"
                placeholder="Rua Exemplo, 123"
                autocomplete="street-address"
              />
            </div>

            <!-- E-mail comercial -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">E-mail comercial</label>
              <input
                v-model="businessEmail"
                type="email"
                class="w-full px-3 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
                maxlength="254"
                placeholder="contato@exemplo.com"
                autocomplete="email"
              />
            </div>

            <!-- Sites -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">Sites</label>
              <div class="space-y-2">
                <div
                  v-for="(website, index) in businessWebsites"
                  :key="`website-${index}`"
                  class="flex flex-col gap-2 sm:flex-row sm:items-center"
                >
                  <input
                    v-model="businessWebsites[index]"
                    type="url"
                    class="flex-1 px-3 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
                    placeholder="https://www.seunegocio.com"
                    inputmode="url"
                    autocomplete="url"
                  />
                  <Button
                    v-if="businessWebsites.length > 1"
                    label="Remover"
                    variant="ghost"
                    color="slate"
                    size="xs"
                    type="button"
                    @click="removeWebsite(index)"
                  >
                    <template #icon>
                      <i class="i-lucide-trash-2" />
                    </template>
                  </Button>
                </div>
              </div>
              <Button
                class="mt-2"
                label="Adicionar site"
                variant="ghost"
                color="blue"
                size="xs"
                type="button"
                @click="addWebsite"
              >
                <template #icon>
                  <i class="i-lucide-plus" />
                </template>
              </Button>
            </div>

            <!-- Segmento do negócio -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">Segmento do negócio</label>
              <select
                v-model="vertical"
                class="w-full px-3 py-2 text-sm bg-n-background border border-n-slate-3 rounded-md focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
              >
                <option
                  v-for="option in verticalOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <!-- Botão de salvar -->
            <div class="flex justify-end">
              <Button
                :label="isSaving ? 'Salvando...' : 'Salvar perfil'"
                variant="solid"
                color="blue"
                size="sm"
                :disabled="isSaving"
                @click="saveProfile"
              >
                <template #icon>
                  <i class="i-lucide-save" />
                </template>
              </Button>
            </div>
          </div>
        </div>

        <!-- Informações da conta -->
        <div class="bg-n-alpha-2 rounded-lg p-6 border border-n-weak">
          <h3 class="text-lg font-medium text-n-slate-12 mb-4 flex items-center gap-2">
            <i class="i-lucide-info" />
            Informações da conta
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-n-slate-11">Tipo de integração:</span>
              <span class="ml-2 font-medium text-n-slate-12">{{ account.integration_type === 'official' ? 'API oficial do WhatsApp Business' : account.integration_type }}</span>
            </div>
            <div>
              <span class="text-n-slate-11">Fornecedor:</span>
              <span class="ml-2 font-medium text-n-slate-12">{{ account.provider || 'whatsapp_cloud_api' }}</span>
            </div>
            <div>
              <span class="text-n-slate-11">ID da integração:</span>
              <span class="ml-2 font-medium text-n-slate-12">{{ account.integration_id }}</span>
            </div>
            <div v-if="account.api_verified !== undefined">
              <span class="text-n-slate-11">API verificada:</span>
              <span class="ml-2 font-medium" :class="account.api_verified ? 'text-grass-700' : 'text-n-amber-11'">
                {{ account.api_verified ? 'Sim' : 'Pendente' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Notice -->
        <div class="bg-n-iris-1 border border-n-iris-4 rounded-lg p-4">
          <div class="flex">
            <i class="i-lucide-info mr-3 mt-0.5 text-n-iris-11"></i>
            <div class="text-sm">
              <p class="font-medium text-n-iris-11 mb-1">Perfil do WhatsApp Business</p>
              <p class="text-n-iris-11">
                As alterações no perfil (imagem, descrição, status, endereço, sites) são sincronizadas com os servidores do WhatsApp
                e ficam visíveis para os clientes quando interagem com a sua empresa.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Estado de não encontrado -->
      <div v-else class="py-8 text-center">
        <div class="text-n-slate-11 mb-4">Conta não encontrada</div>
        <Button
          label="Voltar para contas"
          variant="solid"
          color="blue"
          size="sm"
          @click="goBack"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
